import { BindingGroupModel, ConditionModel, InputGroupModel } from '../models';
import { BindingGroupDto } from '../types';
import { BindingGroupBaseline, serializeBindingGroupContent } from './serializeBindingGroupContent';
import { InputBinding } from '../../iota_applications/types';
import { ExportedTabCondition, TabInputGroupReference, TabStructureType } from '../types/IotaTabExport';

export const getTabStructureType = (group: Pick<InputGroupModel, 'layoutSettingsId'>): TabStructureType => {
    const structure = group.layoutSettingsId ?? 'Basic';
    switch (structure) {
        case 'Basic':
        case 'Layers':
        case 'Sections':
        case 'LayersWithSections':
            return structure;
        default:
            throw new Error(`Cannot transfer tab: unsupported structure "${structure}".`);
    }
};

export const exportConditions = (
    conditions: ConditionModel[],
    groups: InputGroupModel[],
    tags: ReadonlyArray<{ id: string | null; name: string }>
): { content: ExportedTabCondition[]; dependencies: { inputGroups: TabInputGroupReference[] } } => {
    const groupReferences = new Map<string, TabInputGroupReference>();
    const tagsById = new Map(tags.map(tag => [tag.id, tag]));
    const groupsById = new Map(groups.map(group => [group.inputGroupId, group]));
    const content = conditions.map(condition => {
        const tag = tagsById.get(condition.tagId);
        if (!tag) {
            throw new Error(`Cannot export condition "${condition.name}": tag "${condition.tagId}" is unavailable.`);
        }
        const group = groupsById.get(condition.inputGroupId);
        if (!group) {
            throw new Error(`Cannot export condition "${condition.name}": its input group is unavailable.`);
        }
        let reference = groupReferences.get(group.inputGroupId);
        if (!reference) {
            reference = {
                reference: `group-${groupReferences.size + 1}`,
                name: group.name,
                structureType: getTabStructureType(group)
            };
            groupReferences.set(group.inputGroupId, reference);
        }
        return {
            name: condition.name,
            tagName: tag.name,
            searchText: condition.searchText,
            inputGroupReference: reference.reference,
            priority: condition.priority
        };
    });
    return { content, dependencies: { inputGroups: Array.from(groupReferences.values()) } };
};

export const exportBindingGroups = (
    groups: BindingGroupModel[],
    defaults: ReadonlyArray<BindingGroupBaseline>,
    tags: ReadonlyArray<{ id: string | null; name: string }>,
    variables: ReadonlyArray<{ id: string; name: string }>
): BindingGroupDto[] => {
    const tagsById = new Map(tags.map(tag => [tag.id, tag]));
    const variablesById = new Map(variables.map(variable => [variable.id, variable]));
    const exportBinding = (binding: InputBinding): InputBinding => {
        const referenceType = binding.type.toLowerCase();
        if (referenceType !== 'tag' && referenceType !== 'variable') {
            return { ...binding };
        }
        const reference =
            typeof binding.value === 'string'
                ? (referenceType === 'tag' ? tagsById : variablesById).get(binding.value)
                : undefined;
        if (!reference) {
            throw new Error(
                `Cannot export binding "${binding.inputId}": ${referenceType} "${binding.value}" is unavailable.`
            );
        }
        return { ...binding, value: reference.name };
    };

    return serializeBindingGroupContent(groups, defaults, exportBinding);
};
